import { useState } from 'react';
import setDataToGib from '~/client/components/Auth/Shared/utils/setDataToGib';
import useUpdateGtmUserData from '~/client/components/Auth/Shared/utils/useUpdateGtmUserData';
import useUpdateUserLanguage from '~/client/features/SocialAuth/hooks/useUpdateUserLanguage';
import useFeature from '~/client/hooks/feature/useFeature';
import useLanguage from '~/client/hooks/useLanguage';
import useSite from '~/client/hooks/useSite';
import startFraudEngine from '~/client/shared/fraud/fraudEngine';
import { useUpdateUserSettingsMutation } from '~/client/shared/ThemeSwitcher/useThemeSwitcher/graphql/UpdateUserTheme.generated';
import useToken from '~/shared/hooks/useToken';
import trackEvent from '~/shared/trackEvent';
import type { RegisterMutation } from '../graphql/RegisterMutation.generated';
import useHandleSportsbetRegister from '../useHandleSportsbetRegister';
import useRequestEmailVerification from '../useRequestEmailVerification';

const useHandleRegisterResponse = () => {
  const [loading, setLoading] = useState(false);
  const [, setToken] = useToken();
  const [updateUserSettings] = useUpdateUserSettingsMutation();
  const isMostPopularCryptosEnabled = useFeature('isMostPopularCryptosEnabled');
  const { updateGtmUserData } = useUpdateGtmUserData();
  const { requestEmailVerification } = useRequestEmailVerification();
  const { isBridgeSite, site, siteConfig } = useSite();
  const updateUserLanguage = useUpdateUserLanguage();
  const { code: language } = useLanguage();
  const { handleSportsbetRegister } = useHandleSportsbetRegister();

  const handleRegisterResponse = async (
    data: RegisterMutation['userManagementRegisterAsUser'],
  ) => {
    const {
      errors,
      token = '',
      deviceDataToken = '',
      userManagement,
      sessionId,
    } = data ?? {};
    if (errors) {
      setLoading(false);
      return { errors };
    }

    setToken(token);
    setDataToGib(userManagement?.currentProfile?._id || '', sessionId || '');
    if (isMostPopularCryptosEnabled) {
      await updateUserSettings({
        variables: {
          value: 'BTC,ETH,TRX,USDT',
          site,
          name: 'wallets',
        },
      });
    }
    startFraudEngine({ deviceDataToken });
    await updateGtmUserData({ authToken: token });
    trackEvent({}, 'registrationSuccess');
    await requestEmailVerification();

    if (language !== siteConfig.defaultLang) {
      await updateUserLanguage(language);
    }

    if (isBridgeSite) {
      await handleSportsbetRegister();
    }
    return { userManagement };
  };

  return {
    loading,
    handleRegisterResponse,
  };
};

export default useHandleRegisterResponse;
